import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {findShoppingListItemsByQuery} from '../../utils/requestUtils';
import './shoppingList.css';

/**
 * Campo de texto nativo controlado pelo pai (value={textValue}).
 * Evita problemas do react-select que ocultam o texto até o foco.
 * Fluxo de busca/seleção alinhado ao automotivo (digitar → sugestões → escolher).
 */
const ShoppingListItemSelectAsync = forwardRef((props, ref) => {
    const {
        onItemClick,
        placeholder = 'Digite o item...',
        textValue = '',
        style = {},
        dataTest = 'shopping-list-item-async-select',
        onInputValueChange = null,
        onBlur = null,
        excludeNames = [],
    } = props;

    const inputRef = useRef(null);
    const wrapperRef = useRef(null);
    const searchRequestRef = useRef(0);
    const suppressMenuOpenRef = useRef(false);
    const [options, setOptions] = useState([]);
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState(null);

    useImperativeHandle(ref, () => ({
        focus: () => {
            inputRef.current?.focus();
        },
        blur: () => {
            inputRef.current?.blur();
        },
    }));

    const searchItems = useCallback(async (query) => {
        const trimmed = (query || '').trim();
        if (trimmed.length < 1) {
            setOptions([]);
            return;
        }
        const requestId = ++searchRequestRef.current;
        try {
            const names = await findShoppingListItemsByQuery(trimmed);
            if (requestId !== searchRequestRef.current) {
                return;
            }
            const excluded = new Set(
                (excludeNames || [])
                    .filter(Boolean)
                    .map((n) => n.trim().toLowerCase())
            );
            setOptions(
                (names || []).filter((name) => !excluded.has(name.trim().toLowerCase()))
            );
        } catch (error) {
            console.error('Error fetching shopping list items:', error);
            setOptions([]);
        }
    }, [excludeNames]);

    useEffect(() => {
        if (!menuOpen) {
            return undefined;
        }
        const timer = setTimeout(() => searchItems(textValue), 250);
        return () => clearTimeout(timer);
    }, [textValue, menuOpen, searchItems]);

    const updateMenuPosition = useCallback(() => {
        const input = inputRef.current;
        if (!input) {
            return;
        }
        const rect = input.getBoundingClientRect();
        const menuMinWidth = typeof style.menuWidth === 'number'
            ? style.menuWidth
            : parseInt(String(style.menuWidth || ''), 10) || Math.max(rect.width, 280);
        setMenuPosition({
            top: rect.bottom + 2,
            left: rect.left,
            width: Math.max(rect.width, menuMinWidth),
        });
    }, [style.menuWidth]);

    useEffect(() => {
        if (!menuOpen || options.length === 0) {
            return undefined;
        }
        updateMenuPosition();
        const handleReposition = () => updateMenuPosition();
        window.addEventListener('resize', handleReposition);
        window.addEventListener('scroll', handleReposition, true);
        return () => {
            window.removeEventListener('resize', handleReposition);
            window.removeEventListener('scroll', handleReposition, true);
        };
    }, [menuOpen, options.length, updateMenuPosition]);

    useEffect(() => {
        const handlePointerDown = (event) => {
            const target = event.target;
            if (wrapperRef.current?.contains(target)) {
                return;
            }
            if (target instanceof Element && target.closest('.shopping-list-item-select__menu')) {
                return;
            }
            setMenuOpen(false);
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, []);

    const handleChange = (e) => {
        const value = e.target.value;
        if (onInputValueChange) {
            onInputValueChange(value);
        }
        setMenuOpen(true);
    };

    const handleFocus = () => {
        if (suppressMenuOpenRef.current) {
            return;
        }
        setMenuOpen(true);
        searchItems(textValue);
    };

    const handleSelectOption = (name) => {
        suppressMenuOpenRef.current = true;
        setMenuOpen(false);
        setOptions([]);
        if (onItemClick) {
            onItemClick(name);
        }
        setTimeout(() => {
            suppressMenuOpenRef.current = false;
        }, 300);
    };

    const width = style.width || '100%';

    const menuElement = menuOpen && options.length > 0 && menuPosition ? (
        <ul
            className="shopping-list-item-select__menu shopping-list-item-select__menu--portal"
            style={{
                top: menuPosition.top,
                left: menuPosition.left,
                width: menuPosition.width,
                minWidth: menuPosition.width,
            }}
        >
            {options.map((name) => (
                <li
                    key={name}
                    className="shopping-list-item-select__option"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectOption(name);
                    }}
                >
                    {name}
                </li>
            ))}
        </ul>
    ) : null;

    return (
        <div
            ref={wrapperRef}
            className="shopping-list-item-select"
            data-test={dataTest}
            style={{width, maxWidth: style.maxWidth || '100%'}}
        >
            <input
                ref={inputRef}
                type="text"
                className="shopping-list-item-select__input"
                value={textValue}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={() => {
                    if (onBlur) {
                        onBlur();
                    }
                }}
                placeholder={placeholder}
                autoComplete="off"
            />
            {menuElement && createPortal(menuElement, document.body)}
        </div>
    );
});

ShoppingListItemSelectAsync.displayName = 'ShoppingListItemSelectAsync';

export default ShoppingListItemSelectAsync;
